import React, { useContext } from 'react';

import { media } from 'context';
import { Animate, Circle, Icon, Layout, Text } from 'ds';
import { IconName } from 'shared';

import StepContainer from './StepContainer';
import StepTitle from './StepTitle';
import { generateServicesRequestStepPath } from './utils';

interface ServiceStepProps {
  icon: IconName;
  title: string;
  description: string;
  index: number;
}

const ServiceStep: React.FC<ServiceStepProps> = ({ icon, title, description, index }) => (
  <Layout gap={16}>
    <Layout paddingTop={8}>
      <Circle color="purple-gradient" dimension={40}>
        <Icon name={icon} color="blue-200" size={23} />
      </Circle>
    </Layout>
    <Animate paddingY={16} delay={index * 60} duration={300}>
      <Text size="body-md" semibold>
        {title}
      </Text>
      <Text size="body-sm" color="gray-400">
        {description}
      </Text>
    </Animate>
  </Layout>
);

interface Props {}

const ServicesRequestSetUp: React.FC<Props> = () => {
  const { isMobile } = useContext(media);
  const serviceSteps: Omit<ServiceStepProps, 'index'>[] = [
    {
      icon: 'ruler',
      title: 'Generate furniture layouts',
      description: 'Review and approve your custom layouts from design experts'
    },
    {
      icon: 'desk',
      title: 'Recommend furniture items & decor',
      description: 'Select curated furniture sets with exclusive discounts — delivered and installed for you'
    },
    {
      icon: 'wifi',
      title: 'Set up internet & IT',
      description:
        'Choose from available internet services and add professional installation for routers and access points'
    },
    {
      icon: 'lock',
      title: 'Configure vendor & employee access',
      description: 'Select your preferred access method for building and suite access'
    },
    {
      icon: 'featherDuster',
      title: 'Schedule a pre move-in cleaning',
      description: 'Ensure a spotless office awaits you!'
    }
  ];

  return (
    <StepContainer
      step="set-up"
      nextHref={generateServicesRequestStepPath({ step: 'contact' })}
      backHref={generateServicesRequestStepPath({ step: 'move' })}
      stepName="set-up"
      mobileStyle="compact"
      textLayoutProps={isMobile ? { paddingY: 36 } : undefined}
    >
      <StepTitle>I can help you set up your new office.</StepTitle>
      <Layout {...(isMobile ? { paddingX: 24, paddingTop: 24, paddingBottom: 48 } : {})}>
        <Layout paddingBottom={64}>
          <Layout borderRadius={16}>
            <Text size={isMobile ? 'headline-sm' : 'headline-md'} color="purple-gradient">
              How it works
            </Text>
            <Layout position="relative" marginTop={12}>
              {serviceSteps.map((step, index) => (
                <ServiceStep index={index} key={index} {...step} />
              ))}
              <Layout
                color="blue-200"
                position="absolute"
                height={'calc(100% - 54px)'}
                width={4}
                top={20}
                left={18}
                right={0}
                bottom={0}
                zIndex={-1}
              ></Layout>
            </Layout>
          </Layout>
        </Layout>
      </Layout>
    </StepContainer>
  );
};

export default ServicesRequestSetUp;
